import PriceEstimateContextProvider from 'context/PriceEstimateContextProvider';
import useOngoingParkingSession from 'hooks/useOngoingParkingSession';
import useParkingPricingInfo from 'hooks/useParkingPricingInfo';
import SafeAreaHeaderContainer from 'navigation/SafeAreaHeaderContainer';
import React, { useEffect, useMemo } from 'react';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { ColumnWithMargins } from 'styles/ContainerStyles';
import { Maybe, ParkingSession } from 'types/generatedSchemaTypes';
import OngoingParkingActions from './OngoingParkingActions';
import OngoingParkingExtendTime from './OngoingParkingExtendTime';
import OngoingParkingHeader from './OngoingParkingHeader';
import OngoingParkingStatus from './OngoingParkingStatus';
import { AnimatedFullContainer } from './styles';

export const OngoingParkingSession: React.FC<{
  session: Maybe<ParkingSession>;
  showExtendParking: boolean;
  setShowExtendParking: (visibility: boolean) => void;
  sheetExpanded: boolean;
}> = ({ session, showExtendParking, setShowExtendParking, sheetExpanded }) => {
  const { parkingZoneResult } = useOngoingParkingSession();
  const { pricingSegments } = useParkingPricingInfo(
    session?.parkingZoneUid || ''
  );
  const customerSegmentUid = session?.customerSegment?.uid;
  const externalPayment = session?.paymentType === 'SWISH';
  const predefinedTicketsOnly = useMemo(() => {
    return (
      pricingSegments.find(
        (segment) => segment?.uid === (customerSegmentUid || 'Standard')
      )?.predefinedTicketsOnly ?? false
    );
  }, [pricingSegments, customerSegmentUid]);

  useEffect(() => {
    useParkzoneStore
      .getState()
      .setSelectedTariffId(customerSegmentUid ?? undefined);
  }, [customerSegmentUid]);

  useEffect(() => {
    if (!session) {
      return;
    }
    useParkzoneStore
      .getState()
      .setOngoingParkingSessionPriceBreakdownValues({ session });
  }, [session]);

  if (!session) {
    return <></>;
  }

  const isManual =
    session.type === 'START_STOP' ||
    (session.type === 'ANPR' &&
      !!parkingZoneResult?.data?.parkZone?.parkingMethods?.manualStop);
  return (
    <AnimatedFullContainer>
      <SafeAreaHeaderContainer
        useInsetForTop={sheetExpanded}
        animationDuration={300}>
        <OngoingParkingHeader
          session={session}
          isManual={isManual}
          showExtendButton={!predefinedTicketsOnly && !externalPayment}
        />
      </SafeAreaHeaderContainer>
      {showExtendParking ? (
        <PriceEstimateContextProvider>
          <OngoingParkingExtendTime
            testID="extend-ongoing"
            session={session}
            onClose={() => {
              setShowExtendParking(false);
            }}
          />
        </PriceEstimateContextProvider>
      ) : (
        <ColumnWithMargins>
          <OngoingParkingStatus session={session} isManual={isManual} />
          {isManual && (
            <OngoingParkingActions
              onShowExtendTime={() => setShowExtendParking(true)}
              showExtendButton={!predefinedTicketsOnly && !externalPayment}
            />
          )}
        </ColumnWithMargins>
      )}
    </AnimatedFullContainer>
  );
};

export default OngoingParkingSession;
